import type { IssueKind, LotView } from "@hammer/core";
import { agendaBucket, BUCKET_LABELS, type AgendaBucket } from "@hammer/core/time";
import { Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { LotRow } from "../components/LotRow.tsx";
import { useArchived, useArchiveWithUndo, useIssues, useLots, useStanceWithUndo } from "../lib/queries.ts";
import { deleting, held, openPalette, useNow, useStore } from "../lib/store.ts";

const ORDER: AgendaBucket[] = ["overtime", "hour", "today", "tomorrow", "week", "later", "none"];

const ATTENTION: Partial<Record<IssueKind, [string, string]>> = {
  record_result: ["result to record", "results to record"],
  fetch_failing: ["lot Hammer can't check", "lots Hammer can't check"],
  stale: ["price to refresh", "prices to refresh"],
  low_confidence_parse: ["lot to check", "lots to check"],
  suspicious_value: ["odd value", "odd values"],
  end_time_past: ["past end date", "past end dates"],
  source_broken: ["site not answering", "sites not answering"],
};

type Filter = "all" | "bidding" | "watching";

function readPref<T extends string>(k: string, d: T): T {
  try { return (localStorage.getItem(k) as T) ?? d; } catch { return d; }
}
function writePref(k: string, v: string) {
  try { localStorage.setItem(k, v); } catch { /* private mode */ }
}

export function Agenda() {
  const lotsQ = useLots();
  const issues = useIssues().data ?? [];
  const now = useNow();
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState<Filter>(() => readPref("agenda.filter", "all"));
  const [tag, setTag] = useState<string | null>(null);
  const [sel, setSel] = useState(0);
  const navigate = useNavigate();
  const setStance = useStanceWithUndo();
  const archive = useArchiveWithUndo();
  const archivedQ = useArchived();

  // Rows you act on (Pass, Bid, Watch) stay where they are, so nothing jumps or vanishes under the pointer.
  // They settle into place when you change the view or come back to the agenda.
  const heldIds = useStore(held);
  const view = useRef("");
  useEffect(() => held.set(new Map()), []);
  const deletingIds = useStore(deleting);
  const archivedLots = (archivedQ.data ?? []).filter((l) => !deletingIds.has(l.id));

  const all = useMemo(() => (lotsQ.data ?? []).filter((l) => !deletingIds.has(l.id)), [lotsQ.data, deletingIds]);
  const minuteNow = Math.floor(now / 60_000);
  const { priority, groups, flat, passed, closedRecent, tags } = useMemo(() => {
    // Changing what you're looking at (filter, tag, search) is a fresh view: nothing is held in place.
    const key = `${filter}|${tag}|${q}`;
    if (view.current !== key) {
      if (view.current) queueMicrotask(() => held.set(new Map()));
      view.current = key;
    }
    const terms = q.toLowerCase().split(/\s+/).filter(Boolean);
    const tags = [...new Set(all.flatMap((l) => l.tags))].sort();
    const matches = all.filter(
      (l) =>
        l.phase !== "closed" &&
        (!tag || l.tags.includes(tag)) &&
        terms.every((t) => `${l.title} ${l.seller ?? ""} ${l.locationText ?? ""} ${l.tags.join(" ")}`.toLowerCase().includes(t)),
    );
    const sticky = (l: LotView) => heldIds.has(l.id);
    const visible = matches.filter((l) => sticky(l) || (l.stance.stance !== "passed" && (filter === "all" || l.stance.stance === filter)));
    const passed = matches.filter((l) => l.stance.stance === "passed" && !sticky(l));
    // Everything you're bidding on goes in Priority. A held row keeps the section it was in when you acted on it.
    const inPriority = (l: LotView) => (heldIds.get(l.id) ?? (l.stance.stance === "bidding" ? "priority" : "time")) === "priority";
    const byEnd = (a: LotView, c: LotView) => Date.parse(a.latest?.endAt ?? "9999") - Date.parse(c.latest?.endAt ?? "9999") || a.id.localeCompare(c.id);
    const priority = visible.filter(inPriority).sort(byEnd);
    const d = new Date(minuteNow * 60_000);
    const byBucket = new Map<AgendaBucket, LotView[]>();
    for (const l of visible.filter((x) => !inPriority(x))) {
      const b = agendaBucket(l.latest?.endAt ?? null, l.phase, d);
      (byBucket.get(b) ?? byBucket.set(b, []).get(b)!).push(l);
    }
    // Ordered by close time only, so changing a lot's stance never moves it.
    const groups = ORDER.filter((b) => byBucket.has(b)).map((b) => ({
      bucket: b,
      lots: byBucket.get(b)!.sort(byEnd),
    }));
    const closedRecent = all.filter((l) => l.phase === "closed" && l.latest?.endAt && minuteNow * 60_000 - Date.parse(l.latest.endAt) < 3 * 86_400_000).length;
    return { priority, groups, flat: [...priority, ...groups.flatMap((g) => g.lots)], passed, closedRecent, tags };
  }, [all, q, filter, tag, minuteNow, heldIds]);

  // j/k/w/b/p/o/Enter on the selected row
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement).closest("input, textarea, select") || e.metaKey || e.ctrlKey || e.altKey || document.querySelector(".scrim")) return;
      const cur = flat[sel];
      const move = (i: number) => {
        const n = Math.max(0, Math.min(flat.length - 1, i));
        setSel(n);
        document.querySelector(`[data-lot="${flat[n]?.id}"]`)?.scrollIntoView({ block: "nearest" });
      };
      if (e.key === "j") move(sel + 1);
      else if (e.key === "k") move(sel - 1);
      else if (!cur) return;
      else if (e.key === "Enter") void navigate({ to: "/lot/$id", params: { id: cur.id } });
      else if (e.key === "w") setStance(cur, "watching");
      else if (e.key === "b") setStance(cur, "bidding");
      else if (e.key === "p") setStance(cur, "passed");
      else if (e.key === "o") window.open(cur.url, "_blank", "noopener");
      else if (e.key === "e") archive(cur, true);
      else return;
      e.preventDefault();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [flat, sel, navigate, setStance, archive]);

  const attention = Object.entries(
    issues.reduce<Record<string, number>>((acc, i) => ((acc[i.kind] = (acc[i.kind] ?? 0) + 1), acc), {}),
  ).filter(([k]) => ATTENTION[k as IssueKind]);

  if (lotsQ.isPending && !lotsQ.data) return <AgendaSkeleton />;
  if (lotsQ.isError && !lotsQ.data) return <p className="text-red">Couldn't load your lots: {(lotsQ.error as Error).message}</p>;

  if (!all.length) {
    return (
      <div className="max-w-[34rem] mt-[10vh] grid gap-4">
        <h1 className="heading text-[31px] leading-tight">Nothing tracked yet</h1>
        <p className="muted">Paste a link to any auction lot to start. Hammer checks prices for you, counts down to the close, and tells you when its numbers go out of date.</p>
        <div className="flex gap-2 flex-wrap">
          <button className="btn btn-primary" onClick={() => openPalette()}>Add a lot</button>
          <Link to="/sources" className="btn">Install the bookmarklet</Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      {attention.length > 0 && (
        <Link to="/inbox" className="attention">
          <strong>{issues.length} need{issues.length === 1 ? "s" : ""} you</strong>
          <span className="muted">
            {attention.map(([k, n]) => `${n} ${ATTENTION[k as IssueKind]![n === 1 ? 0 : 1]}`).join(", ")}
          </span>
        </Link>
      )}

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <input id="search" className="field max-w-[18rem]" placeholder="Search your lots" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search your lots" />
        <div className="tabs" role="group" aria-label="Show">
          {(["all", "bidding", "watching"] as Filter[]).map((f) => (
            <button key={f} aria-pressed={filter === f} onClick={() => { setFilter(f); writePref("agenda.filter", f); }}>
              {f === "all" ? "All" : f === "bidding" ? "Bidding" : "Watching"}
            </button>
          ))}
        </div>
      </div>
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-3" role="group" aria-label="Filter by tag">
          {tags.map((t) => (
            <button key={t} className="chip" aria-pressed={tag === t} onClick={() => setTag(tag === t ? null : t)}>{t}</button>
          ))}
        </div>
      )}

      {priority.length > 0 && (
        <section aria-label="Priority" className="priority">
          <h2 className="group-h heading">
            Priority<span className="n num">{priority.length}</span>
            <span className="text-[13.5px] font-normal muted" style={{ fontVariationSettings: '"wdth" 100' }}>Everything you're bidding on, soonest first</span>
          </h2>
          <div role="grid">
            {priority.map((l) => {
              const i = flat.indexOf(l);
              return <LotRow key={l.id} lot={l} selected={i === sel} onFocus={() => setSel(i)} />;
            })}
          </div>
        </section>
      )}
            {groups.map((g) => (
        <section key={g.bucket} aria-label={BUCKET_LABELS[g.bucket]}>
          <h2 className="group-h heading">
            {BUCKET_LABELS[g.bucket]}
            <span className="n num">{g.lots.length}</span>
          </h2>
          <div role="grid">
            {g.lots.map((l) => {
              const i = flat.indexOf(l);
              return <LotRow key={l.id} lot={l} selected={i === sel} onFocus={() => setSel(i)} />;
            })}
          </div>
        </section>
      ))}
      {!groups.length && <p className="muted mt-8">No open lots match.</p>}
      {passed.length > 0 && (
        <details className="mt-10 group">
          <summary className="group-h heading cursor-pointer list-none !mt-0">
            Passed<span className="n num">{passed.length}</span>
            <span className="text-[13.5px] font-normal muted" style={{ fontVariationSettings: '"wdth" 100' }}>Still closing; you've decided against them</span>
          </summary>
          {passed.map((l) => <LotRow key={l.id} lot={l} />)}
        </details>
      )}
      {archivedLots.length > 0 && (
        <details className="mt-10">
          <summary className="group-h heading cursor-pointer list-none !mt-0">
            Archived<span className="n num">{archivedLots.length}</span>
            <span className="text-[13.5px] font-normal muted" style={{ fontVariationSettings: '"wdth" 100' }}>Off the agenda and not checked; Restore brings one back</span>
          </summary>
          {archivedLots.map((l) => <LotRow key={l.id} lot={l} />)}
        </details>
      )}
            {closedRecent > 0 && (
        <p className="mt-8 muted text-[14px]">
          {closedRecent} lot{closedRecent === 1 ? "" : "s"} closed in the last three days. <Link to="/history" className="underline underline-offset-2">See results in History</Link>
        </p>
      )}
    </div>
  );
}

function AgendaSkeleton() {
  return (
    <div aria-busy>
      <div className="skeleton h-9 w-72 mb-8" />
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={i} className="lot-row">
          <div className="skeleton thumb r-img" />
          <div className="r-main grid gap-2"><div className="skeleton h-4 w-3/4" /><div className="skeleton h-3 w-1/2" /></div>
          <div className="r-price"><div className="skeleton h-5 w-20 ml-auto" /></div>
          <div className="r-time"><div className="skeleton h-5 w-16 ml-auto" /></div>
          <div className="r-act"><div className="skeleton h-8 w-44" /></div>
        </div>
      ))}
    </div>
  );
}
